import { Tooltip } from 'radix-ui'
import { createContext, useContext, type ComponentType, type ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Navegação — componente 07.
 * Sidebar de 200px em pampas com fio à direita; dois grupos (ADMINISTRAÇÃO · OPERAÇÃO) em rótulo espaçado.
 * Item com min-height 38px nos DOIS estados; ativo em ink sólido com texto pampas em Lexend 400.
 * Contador de pendência em plum ao lado do rótulo — só onde existe pendência.
 * Recolhida (76px): só ícone de 20px, nome no tooltip após 400ms, contador como etiqueta de 16px AO LADO
 * do ícone. Rótulo abreviado ("Dash", "Assin") é proibido. Os títulos de grupo viram um fio de 1px.
 * Abaixo de 900px a sidebar sai de cena e volta como gaveta (`open`), com `BottomNav` fixa.
 */

interface SidebarContextValue {
  collapsed: boolean
}

const SidebarContext = createContext<SidebarContextValue>({ collapsed: false })

export interface SidebarProps {
  collapsed?: boolean
  onToggle?: () => void
  /** Gaveta aberta no celular (<900px). */
  open?: boolean
  onClose?: () => void
  /** Wordmark (aberta) — o símbolo é usado quando recolhida. */
  brand: ReactNode
  brandCollapsed: ReactNode
  /** Rodapé: usuário, clínica, sair. */
  footer?: ReactNode
  children: ReactNode
  className?: string
}

export function Sidebar({
  collapsed = false,
  onToggle,
  open = false,
  onClose,
  brand,
  brandCollapsed,
  footer,
  children,
  className,
}: SidebarProps) {
  return (
    <SidebarContext.Provider value={{ collapsed }}>
      {open ? (
        <button
          type="button"
          aria-label="Fechar menu"
          onClick={onClose}
          className="bg-overlay desk:hidden fixed inset-0 z-30"
        />
      ) : null}
      <aside
        data-slot="sidebar"
        data-collapsed={collapsed || undefined}
        className={cn(
          'border-hairline bg-pampas relative flex shrink-0 flex-col gap-px border-r py-[26px] transition-[width] duration-[180ms]',
          collapsed ? 'w-sidebar-collapsed px-2' : 'w-sidebar px-[14px]',
          // Desktop: fixa na altura da janela; só o conteúdo da página rola (07-responsividade).
          // Fica acima do cabeçalho da página (z-10), para o botão de recolher não ser coberto.
          'desk:sticky desk:top-0 desk:z-20 desk:h-dvh desk:self-start',
          // Celular: gaveta fixa sobre o conteúdo.
          'max-desk:fixed max-desk:inset-y-0 max-desk:left-0 max-desk:z-40 max-desk:w-sidebar max-desk:px-[14px] max-desk:transition-transform',
          open ? 'max-desk:translate-x-0' : 'max-desk:-translate-x-full',
          className,
        )}
      >
        {/* Fica sobre o fio da sidebar e por cima do cabeçalho fixo da página (z-10). */}
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? 'Abrir menu' : 'Recolher menu'}
            aria-expanded={!collapsed}
            title="Atalho: ["
            className="bg-ink font-body text-eyebrow text-pampas max-desk:hidden absolute top-[22px] -right-[11px] z-20 size-[22px] cursor-pointer text-center leading-[22px] font-light tracking-normal hover:opacity-85"
          >
            {collapsed ? '›' : '‹'}
          </button>
        ) : null}
        <div className={cn('mb-7', collapsed ? 'flex justify-center' : 'px-3')}>
          {collapsed ? brandCollapsed : brand}
        </div>
        <Tooltip.Provider delayDuration={400}>
          <nav className="flex flex-1 flex-col gap-px">{children}</nav>
        </Tooltip.Provider>
        {footer ? <div className="border-hairline mt-auto border-t pt-5">{footer}</div> : null}
      </aside>
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  return useContext(SidebarContext)
}

export interface SidebarGroupProps {
  label: string
  children: ReactNode
  className?: string
}

export function SidebarGroup({ label, children, className }: SidebarGroupProps) {
  const { collapsed } = useSidebar()
  return (
    <div
      data-slot="sidebar-group"
      className={cn('flex flex-col gap-px not-first:mt-[22px]', className)}
    >
      {collapsed ? (
        <div aria-hidden className="bg-hairline-strong mx-[10px] mb-[10px] h-px" />
      ) : (
        <div className="font-body text-eyebrow text-label-soft mb-[10px] ml-3 font-light tracking-[.2em] uppercase">
          {label}
        </div>
      )}
      {children}
    </div>
  )
}

export interface SidebarItemProps {
  label: string
  icon: ReactNode
  active?: boolean
  /** Contador de pendência em plum. Só passe quando existe pendência. */
  badge?: number | string
  href?: string
  onClick?: () => void
  /** Componente de link (ex.: `Link` do React Router). Sem ele, `href` vira `<a>`. */
  LinkComponent?: ComponentType<{
    to: string
    className?: string
    children: ReactNode
    'aria-current'?: 'page'
    onClick?: () => void
    title?: string
  }>
}

export function SidebarItem({
  label,
  icon,
  active,
  badge,
  href,
  onClick,
  LinkComponent,
}: SidebarItemProps) {
  const { collapsed } = useSidebar()
  const hasBadge = badge !== undefined && badge !== null && badge !== 0 && badge !== ''

  const className = cn(
    'flex min-h-nav-item max-desk:min-h-touch cursor-pointer items-center gap-[10px] transition-[background-color] hover:bg-ink/6 focus-visible:outline-offset-[-1px]',
    collapsed ? 'justify-center gap-[5px] px-1' : 'justify-between px-3',
    active
      ? 'bg-ink font-display text-[13px] leading-[1.45] font-normal text-pampas hover:bg-ink'
      : 'font-body text-[13px] leading-[1.45] font-light text-ink',
  )

  const badgeNode = hasBadge ? (
    <span
      data-slot="sidebar-badge"
      className={cn(
        'font-body text-eyebrow font-light tracking-normal',
        collapsed
          ? cn(
              'h-4 min-w-4 px-[3px] text-center leading-4',
              active ? 'bg-pampas text-ink' : 'bg-plum text-pampas',
            )
          : active
            ? 'text-pampas'
            : 'text-plum',
      )}
    >
      {badge}
    </span>
  ) : null

  const content = collapsed ? (
    <>
      <span className="flex size-5 items-center justify-center [&>svg]:size-5">{icon}</span>
      {badgeNode}
    </>
  ) : (
    <>
      <span className="flex min-w-0 items-center gap-[10px]">
        <span className="flex size-5 shrink-0 items-center justify-center [&>svg]:size-5">
          {icon}
        </span>
        <span className="truncate">{label}</span>
      </span>
      {badgeNode}
    </>
  )

  const ariaLabel = hasBadge ? `${label}, ${badge} pendentes` : label

  const element =
    href && LinkComponent ? (
      <LinkComponent
        to={href}
        className={className}
        aria-current={active ? 'page' : undefined}
        onClick={onClick}
        title={collapsed ? undefined : undefined}
      >
        {content}
      </LinkComponent>
    ) : href ? (
      <a
        href={href}
        className={className}
        aria-current={active ? 'page' : undefined}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {content}
      </a>
    ) : (
      <button
        type="button"
        className={cn(className, 'w-full text-left')}
        aria-current={active ? 'page' : undefined}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {content}
      </button>
    )

  if (!collapsed) return element

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{element}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="right"
          sideOffset={8}
          className="bg-ink font-body text-pampas shadow-float z-50 px-3 py-2 text-[12px] leading-none font-light"
        >
          {label}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  )
}

export interface SidebarUserProps {
  name: string
  clinic: string
  onSignOut?: () => void
  /** Quando há mais de um lugar onde a pessoa pode estar: a ação ao lado do nome do lugar atual. */
  onSwitch?: () => void
  switchLabel?: string
  /** A página da própria pessoa: com ele, o nome (e o avatar, recolhida) vira link. */
  accountHref?: string
  /** O que o link do nome leva a ver, para quem navega sem o mouse. */
  accountLabel?: string
  /** Componente de link (ex.: `Link` do React Router). Sem ele, `accountHref` vira `<a>`. */
  LinkComponent?: SidebarItemProps['LinkComponent']
}

/**
 * Rodapé da sidebar: nome em Lexend, clínica em rótulo, "sair" em plum. Com `onSwitch`, a ação de
 * trocar aparece ao lado da clínica; com `accountHref`, o nome leva à página da pessoa. Recolhida,
 * a sidebar mostra o avatar (link da conta, se houver) e "sair".
 */
export function SidebarUser({
  name,
  clinic,
  onSignOut,
  onSwitch,
  switchLabel = 'trocar',
  accountHref,
  accountLabel = 'Sua conta',
  LinkComponent,
}: SidebarUserProps) {
  const { collapsed } = useSidebar()

  // O nome (ou o avatar) como link: pelo roteador quando ele existe, senão como âncora comum.
  const accountLink = (className: string, children: ReactNode) => {
    if (!accountHref) return null
    return LinkComponent ? (
      <LinkComponent to={accountHref} className={className} title={accountLabel}>
        {children}
      </LinkComponent>
    ) : (
      <a href={accountHref} className={className} title={accountLabel}>
        {children}
      </a>
    )
  }

  if (collapsed) {
    const avatar = <span aria-hidden className="bg-ink block size-[26px] rounded-full" />
    return (
      <div className="flex w-full flex-col items-center gap-[6px]">
        {accountLink(
          'block',
          <>
            {avatar}
            <span className="sr-only">{accountLabel}</span>
          </>,
        ) ?? avatar}
        <button
          type="button"
          onClick={onSignOut}
          className="font-body text-eyebrow text-plum hover:text-ink cursor-pointer font-light tracking-normal"
        >
          sair
        </button>
      </div>
    )
  }
  const nameClass = 'font-display text-ink block truncate text-[13px] leading-[1.3] font-normal'
  return (
    <div className="flex items-end justify-between gap-[10px] px-3">
      <div className="min-w-0">
        {accountLink(
          cn(
            nameClass,
            "hover:text-ink/70 max-desk:relative max-desk:after:absolute max-desk:after:inset-x-0 max-desk:after:top-1/2 max-desk:after:h-touch max-desk:after:-translate-y-1/2 max-desk:after:content-['']",
          ),
          name,
        ) ?? <div className={nameClass}>{name}</div>}
        <div className="font-body text-eyebrow text-label-soft mt-1 flex items-baseline gap-[6px] leading-[1.5] font-light tracking-normal">
          <span className="truncate">{clinic}</span>
          {onSwitch ? (
            <button
              type="button"
              onClick={onSwitch}
              className="text-ink border-hairline-heavy hover:border-ink max-desk:relative max-desk:after:absolute max-desk:after:inset-x-0 max-desk:after:top-1/2 max-desk:after:h-touch max-desk:after:-translate-y-1/2 max-desk:after:content-[''] shrink-0 cursor-pointer border-b whitespace-nowrap"
            >
              {switchLabel}
            </button>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        onClick={onSignOut}
        className="font-body text-eyebrow text-plum hover:text-ink max-desk:relative max-desk:after:absolute max-desk:after:inset-x-0 max-desk:after:top-1/2 max-desk:after:h-touch max-desk:after:-translate-y-1/2 max-desk:after:content-[''] cursor-pointer font-light tracking-normal whitespace-nowrap"
      >
        sair
      </button>
    </div>
  )
}

export interface BottomNavItem {
  label: string
  icon: ReactNode
  href: string
  active?: boolean
}

export interface BottomNavProps {
  items: BottomNavItem[]
  LinkComponent?: ComponentType<{
    to: string
    className?: string
    children: ReactNode
    'aria-current'?: 'page'
  }>
  className?: string
}

/** Barra inferior fixa do celular: quatro rotas (Hoje · Pacientes · Assinaturas · Planos), a atual com fio superior de 2px. */
export function BottomNav({ items, LinkComponent, className }: BottomNavProps) {
  return (
    <nav
      data-slot="bottom-nav"
      aria-label="Navegação principal"
      className={cn(
        'border-hairline bg-surface desk:hidden fixed inset-x-0 bottom-0 z-20 grid border-t',
        className,
      )}
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    >
      {items.map((item) => {
        const itemClass = cn(
          'flex min-h-touch flex-col items-center justify-center gap-1 border-t-2 font-body text-eyebrow font-light tracking-normal',
          item.active ? 'border-ink text-ink' : 'border-transparent text-label',
        )
        const inner = (
          <>
            <span className="[&>svg]:size-5">{item.icon}</span>
            {item.label}
          </>
        )
        return LinkComponent ? (
          <LinkComponent
            key={item.href}
            to={item.href}
            className={itemClass}
            aria-current={item.active ? 'page' : undefined}
          >
            {inner}
          </LinkComponent>
        ) : (
          <a
            key={item.href}
            href={item.href}
            className={itemClass}
            aria-current={item.active ? 'page' : undefined}
          >
            {inner}
          </a>
        )
      })}
    </nav>
  )
}
